import { secondAxios } from "../../../../../../../api/axios";
import abbreviationList from "./abbreviation.json";

export const documentAbbreviations = Array.isArray(abbreviationList)
  ? abbreviationList
  : [];

const abbreviationByCode = new Map(
  documentAbbreviations.map((item) => [
    String(item.abbreviation ?? "").trim(),
    item,
  ]),
);

const abbreviationByDescription = new Map(
  documentAbbreviations.map((item) => [
    String(item.description ?? "")
      .trim()
      .toUpperCase(),
    String(item.abbreviation ?? "").trim(),
  ]),
);

/** Map stored documentName to abbreviation code when possible. */
export const resolveDocumentNameAbbreviation = (raw) => {
  const value = String(raw ?? "").trim();
  if (!value) return "";
  if (abbreviationByCode.has(value)) return value;
  const fromDescription = abbreviationByDescription.get(value.toUpperCase());
  return fromDescription ?? value;
};

/** Resolve stored documentName / abbr to the full identifier description. */
export const resolveDocumentNameDescription = (raw) => {
  const value = String(raw ?? "").trim();
  if (!value) return "";
  const abbr = resolveDocumentNameAbbreviation(value);
  const known = abbreviationByCode.get(abbr);
  if (known?.description) return String(known.description).trim();
  // Already a description, or unknown legacy value
  if (abbreviationByDescription.has(value.toUpperCase())) return value;
  return value;
};

/**
 * UID from full document abbreviation + sequence (e.g. PRC + 101 → PRC-101).
 */
export const buildDocumentUid = (documentNameOrAbbr, sequence) => {
  const abbr = resolveDocumentNameAbbreviation(documentNameOrAbbr);
  const seq =
    sequence != null && String(sequence).trim() !== ""
      ? String(sequence).trim()
      : "";
  if (!abbr && !seq) return "";
  if (!abbr) return seq;
  if (!seq) return abbr;
  return `${abbr}-${seq}`;
};

const readSequenceValue = (doc) => {
  if (!doc || typeof doc !== "object") return "";
  const v = doc.sequence ?? doc.sequenceNumber ?? doc.number ?? doc.seq;
  return v != null && String(v).trim() !== "" ? String(v).trim() : "";
};

/**
 * projectNumber + abbreviation + uid
 * e.g. 1001-PDS-V-101
 */
/** UID for schedule/task rows: direct field or match document register by task code / title. */
export function resolveTaskUidForTask(task, documents = []) {
  if (!task) return "";
  const direct = String(task.uid ?? "").trim();
  if (direct) return direct;
  const key = String(task.taskCode ?? task.task ?? "").trim();
  if (!key) return "";
  const doc = (documents || []).find((d) => {
    const title = String(d?.title ?? d?.document ?? "").trim();
    const docNum = String(d?.docNumber ?? d?.documentNumber ?? "").trim();
    return title === key || docNum === key;
  });
  return doc?.uid != null ? String(doc.uid).trim() : "";
}

export const buildRegisterDocNumber = (doc) => {
  if (!doc || typeof doc !== "object") return "";
  const documentName = resolveDocumentNameAbbreviation(
    doc.documentName ?? doc.department,
  );
  const sequence = readSequenceValue(doc);
  // UID already includes full abbreviation + sequence (e.g. PRC-101)
  const uid =
    buildDocumentUid(documentName, sequence) ||
    (doc.uid != null ? String(doc.uid).trim() : "");
  const parts = [doc.projectNumber, uid]
    .map((s) => (s != null ? String(s).trim() : ""))
    .filter(Boolean);
  return parts.join("-");
};

/** Merge persisted row with in-progress edit values for live Document # preview. */
export const getRegisterDocSource = (row) => {
  const original = row?.original ?? row ?? {};
  const cache = row?._valuesCache ?? {};
  const documentName =
    cache.documentName ?? original.documentName ?? original.department;
  const sequence = readSequenceValue({ ...original, ...cache });
  const uid =
    buildDocumentUid(documentName, sequence) ||
    String(cache.uid ?? original.uid ?? "").trim();
  return {
    projectNumber: cache.projectNumber ?? original.projectNumber,
    documentName,
    sequence,
    uid,
  };
};

/** `@IsInt` on CreateDocumentDto — send a JSON number or omit. */
export const normalizeNumberOfSheets = (value) => {
  if (value === "" || value === null || value === undefined) return undefined;
  const n =
    typeof value === "number" ? value : parseInt(String(value).trim(), 10);
  if (!Number.isFinite(n) || n < 0) return undefined;
  return n;
};

/**
 * Builds body for POST/PATCH per CreateDocumentDto / UpdateDocumentDto (whitelist-safe).
 * Omits blank optional fields; sends integer `numberOfSheets` when set.
 */
export const buildDocumentMutationBody = (raw, { isCreate } = { isCreate: false }) => {
  const trim = (s) => (s == null ? "" : String(s).trim());

  const companyName = trim(raw.companyName);
  const projectNumber = trim(raw.projectNumber);
  const documentName = resolveDocumentNameAbbreviation(
    trim(raw.documentName ?? raw.department),
  );

  const sequenceRaw =
    raw.sequence ?? raw.sequenceNumber ?? raw.number ?? raw.seq;
  let sequenceForUid = "";
  if (
    sequenceRaw !== undefined &&
    sequenceRaw !== null &&
    String(sequenceRaw).trim() !== ""
  ) {
    sequenceForUid = String(sequenceRaw).trim();
  }

  const uid =
    buildDocumentUid(documentName, sequenceForUid) ||
    (raw.uid != null ? String(raw.uid).trim() : "");

  const body = {};

  if (raw.type != null && String(raw.type).trim() !== "") {
    body.type = String(raw.type).trim();
  }

  const documentNumber = buildRegisterDocNumber({
    projectNumber,
    documentName,
    sequence: sequenceForUid,
    uid,
  });
  if (documentNumber) body.documentNumber = documentNumber;
  if (companyName) body.companyName = companyName;
  if (projectNumber) body.projectNumber = projectNumber;
  if (documentName) body.documentName = documentName;
  if (uid !== "") body.uid = uid;

  if (sequenceForUid !== "") {
    const seqNum = parseInt(sequenceForUid, 10);
    if (Number.isFinite(seqNum)) {
      body.sequence = seqNum;
      body.number = seqNum;
    } else {
      body.sequence = sequenceForUid;
    }
  }

  const rev = raw.revision;
  if (rev !== undefined && rev !== null && String(rev).trim() !== "") {
    body.revision = String(rev).trim();
  }

  const description = raw.description ?? raw.title;
  if (
    description !== undefined &&
    description !== null &&
    String(description).trim() !== ""
  ) {
    body.description = String(description).trim();
  }

  const numberOfSheets = normalizeNumberOfSheets(raw.numberOfSheets);
  if (numberOfSheets !== undefined) body.numberOfSheets = numberOfSheets;

  const responsible = raw.responsible;
  if (responsible !== undefined && responsible !== null && trim(responsible)) {
    body.responsible = trim(responsible);
  }

  return body;
};

const fetchDocumentsByProject = async (projectId, type) => {
  if (!projectId) return [];
  const documentType = type || "all";
  const response = (
    await secondAxios.get(`/documents/${projectId}/type/${documentType}`)
  ).data;
  return response;
};

/** Dedupe concurrent plant/project document list fetches (plant mode is N+1 HTTP). */
const documentListInflight = new Map();

const runDeduped = (key, loader) => {
  const existing = documentListInflight.get(key);
  if (existing) return existing;

  const promise = loader().finally(() => {
    if (documentListInflight.get(key) === promise) {
      documentListInflight.delete(key);
    }
  });

  documentListInflight.set(key, promise);
  return promise;
};

export const invalidateDocumentListCache = () => {
  documentListInflight.clear();
};

const loadPlantDocumentList = async (type, plantId) => {
  if (!plantId) return [];
  try {
    const res = await secondAxios.get(`/plant/${plantId}`);
    const projectsRaw =
      res.data?.projects ||
      (Array.isArray(res.data?.project)
        ? res.data.project
        : res.data?.project
        ? [res.data.project]
        : Array.isArray(res.data)
        ? res.data
        : []);

    if (!Array.isArray(projectsRaw) || projectsRaw.length === 0) {
      return [];
    }

    const filteredProjects = projectsRaw.filter(
      (p) => p.clientName === process.env.REACT_APP_CLIENT_NAME
    );

    if (filteredProjects.length === 0) {
      return [];
    }

    const docsByProject = await Promise.all(
      filteredProjects.map(async (project) => {
        const docs = await fetchDocumentsByProject(project?.id, type);
        return docs.map((doc) => ({
          ...doc,
          projectId: project?.id,
          projectName: project?.name,
        }));
      })
    );

    const allDocuments = docsByProject.flat();

    const seen = new Set();
    const uniqueDocuments = allDocuments.filter((doc) => {
      const label =
        doc?.documentNumber ??
        doc?.docNumber ??
        doc?.description ??
        doc?.title ??
        "";
      const key = String(label).toLowerCase().trim();
      if (!key) return true;
      if (seen.has(key)) return false;
      seen.add(key);
      return true;
    });

    return uniqueDocuments;
  } catch (error) {
    console.error("Failed to load plant documents", error);
    return [];
  }
};

export const getProjectDocumentList = async (type, projectId) => {
  const normalizedType = type || "all";
  const key = `project|${normalizedType}|${projectId ?? ""}`;
  return runDeduped(key, () => fetchDocumentsByProject(projectId, normalizedType));
};

export const getPlantDocumentList = async (type, plantId) => {
  const normalizedType = type || "all";
  const key = `plant|${normalizedType}|${plantId ?? ""}`;
  return runDeduped(key, () => loadPlantDocumentList(normalizedType, plantId));
};

export const updateDocumentList = async (updateDocument) => {
  const body = buildDocumentMutationBody(updateDocument, { isCreate: false });
  await secondAxios.patch(`/documents/${updateDocument.id}`, body);
  invalidateDocumentListCache();
};

export const createDocumentList = async (type, projectId, newDoc) => {
  const body = buildDocumentMutationBody(
    { ...newDoc, type: newDoc.type ?? type },
    { isCreate: true },
  );
  await secondAxios.post(`/documents/${projectId}`, body);
  invalidateDocumentListCache();
};

export const deleteDocumentList = async (id) => {
  try {
    await secondAxios.delete(`/documents/${id}`);
    invalidateDocumentListCache();
  } catch (error) {
    console.log(error);
  }
};
